import React, { useState, useEffect, useRef } from 'react';
import { Bot, User, Terminal, FileText } from 'lucide-react';
import { MarkdownRenderer } from "./MarkdownRenderer";
import { toolRenderers, ToolRendererProps, DefaultToolCallRenderer, DefaultToolResponseRenderer } from "./ToolRenderers";
import CustomAxios from "../../utility/customAxios";
import useAuth from "hooks/useAuth";
import { VisibleScrollArea } from "./toolRenderers/VisibleScrollArea";

// Message interface that matches the one in GuardianChat
export interface ChatMessage {
    id?: string;
    role: 'user' | 'assistant' | 'system' | 'tool';
    content: string;
    tool_name?: string;
    tool_call?: boolean;
    tool_call_id?: string;
    tool_call_function_name?: string;
    tool_call_function_args?: string;
    tool_call_response_id?: string;
    tool_call_response_content?: string;
    screenshot?: string;
    created_at?: string;
}

interface ChatMessageStreamProps {
    chatId: string;
    className?: string;
    maxHeight?: string | number;
    autoScroll?: boolean;
    initialMessages?: ChatMessage[];
    dontUpdate?: boolean; // Optional prop to control whether to update messages periodically
}

/**
 * Function to check if a tool message should be rendered by a custom renderer
 */
function wouldRenderToolMessage(message: ChatMessage): boolean {
    // Check if there's a custom renderer for this tool
    const toolName = message.tool_name;
    const CustomRenderer = toolName && toolRenderers[toolName];
    
    // If the custom renderer has a shouldRender method, use it
    if (CustomRenderer && typeof CustomRenderer.shouldRender === 'function') {
        return CustomRenderer.shouldRender(message);
    }
    
    // By default, render all tool messages
    return true;
}

/**
 * Component to render a tool message with the appropriate renderer
 */
function ToolMessageRenderer({ message, isToolCall }: { message: ChatMessage, isToolCall: boolean }) {
    // Check if there's a custom renderer for this tool
    const toolName = isToolCall ? message.tool_call_function_name : message.tool_name;
    const CustomRenderer = toolName && toolRenderers[toolName];
    
    // If we have a custom renderer for this tool, use it
    if (CustomRenderer) {
        return <CustomRenderer message={message} isToolCall={isToolCall} />;
    }
    
    // Otherwise use the default renderers
    if (message.tool_call_function_name) {
        return <DefaultToolCallRenderer message={message} />;
    } else {
        return <DefaultToolResponseRenderer message={message} />;
    }
}

export function ChatMessageStream({ 
    chatId, 
    className = "", 
    maxHeight = "500px",
    autoScroll = true,
    initialMessages = [],
    dontUpdate = false
}: ChatMessageStreamProps) {
    const { user } = useAuth();
    const [messages, setMessages] = useState<ChatMessage[]>(initialMessages);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [eventSource, setEventSource] = useState<EventSource | null>(null);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const scrollAreaRef = useRef<HTMLDivElement>(null);
    const [isNearBottom, setIsNearBottom] = useState(true);
    const [retryCount, setRetryCount] = useState(0);
    const MAX_RETRY_COUNT = 3;

    // Load messages on initial load and when chatId changes
    useEffect(() => {
        if (!chatId) return;
        
        setRetryCount(0);
        setIsLoading(true);
        setError(null);
        
        const fetchMessages = async () => {
            try {
                // Use the correct API endpoint
                const response = await CustomAxios.get(`/api/v1/guardian/chat?id=${chatId}`);
                if (response.data && response.data.messages && Array.isArray(response.data.messages)) {
                    setMessages(response.data.messages);
                }
                setIsLoading(false);
            } catch (error) {
                console.error("Error fetching chat messages:", error);
                setError("Failed to load conversation");
                setIsLoading(false);
            }
        };
        
        fetchMessages();
        
        // Set up streaming updates
        setupEventSource();
        
        return () => {
            // Clean up event source on unmount or chatId change
            if (eventSource) {
                eventSource.close();
                setEventSource(null);
            }
        };
    }, [chatId]);
    
    // Scroll to bottom when messages change
    useEffect(() => {
        if (autoScroll && isNearBottom) {
            scrollToBottom();
        }
    }, [messages, autoScroll]);
    
    // Check if we're near the bottom of the scroll area
    useEffect(() => {
        const scrollArea = scrollAreaRef.current;
        if (!scrollArea) return;
        
        const scrollElement = scrollArea.querySelector('[data-radix-scroll-area-viewport]');
        if (!scrollElement) return;
        
        const checkIfNearBottom = () => {
            const { scrollHeight, scrollTop, clientHeight } = scrollElement as HTMLElement;
            // Consider "near bottom" if within 50px of the bottom
            const isNear = scrollHeight - scrollTop - clientHeight < 50;
            setIsNearBottom(isNear);
        };
        
        scrollElement.addEventListener('scroll', checkIfNearBottom);
        
        return () => {
            scrollElement.removeEventListener('scroll', checkIfNearBottom);
        };
    }, []);
    
    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };
    
    const setupEventSource = () => {
        if (!chatId) return;

        if (dontUpdate) {
            return;
        }
        
        console.log(`Setting up EventSource for chat ID: ${chatId}`);
        
        // Close any existing event source
        if (eventSource) {
            eventSource.close();
        }
        
        // POST to initiate streaming - we need to set up a function to handle this differently
        // since the current endpoint only supports initiating a new chat
        console.log("Note: Streaming from existing chat not supported by current API endpoints");
        
        // This is a placeholder - the actual implementation would need to be updated
        // once the API supports streaming from existing chats
        const checkForUpdates = async () => {
            try {
                // Periodically poll for updates instead
                const response = await CustomAxios.get(`/api/v1/guardian/chat?id=${chatId}`);
                if (response.data && response.data.messages && Array.isArray(response.data.messages)) {
                    setMessages(response.data.messages);
                }
            } catch (error) {
                console.error("Error polling for updates:", error);
            }
        };
        
        // Poll every 3 seconds for updates
        const intervalId = setInterval(checkForUpdates, 3000);
        
        // Return cleanup function
        return () => {
            clearInterval(intervalId);
        };
    };
    
    // Get the previous tool name for context
    const getPrevToolName = (index: number): string | undefined => {
        // Look backward from the current message to find the last tool name
        for (let i = index - 1; i >= 0; i--) {
            const prevMsg = messages[i];
            if (prevMsg.tool_name) {
                return prevMsg.tool_name;
            }
            if (prevMsg.tool_call_function_name) {
                return prevMsg.tool_call_function_name;
            }
        }
        return undefined;
    };
    
    return (
        <div className={`flex flex-col w-full ${className}`}>
            {isLoading && messages.length === 0 ? (
                <div className="flex justify-center items-center h-20">
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
                </div>
            ) : error ? (
                <div className="p-4 text-red-400">
                    {error}
                </div>
            ) : (
                <VisibleScrollArea 
                    className="w-full overflow-hidden pr-1"
                    style={{ maxHeight, height: '100%' }}
                    ref={scrollAreaRef}
                >
                    <div className="divide-y divide-[#1D2739]">
                        {messages.map((message, index) => {
                            // Skip messages that shouldn't be rendered
                            if (message.tool_name && !wouldRenderToolMessage(message)) {
                                return null;
                            }
                            
                            // Get previous tool name for context
                            const prevToolName = getPrevToolName(index);
                            
                            // Create enhanced message
                            const enhancedMessage = {
                                ...message,
                                tool_name: message.tool_name || message.tool_call_function_name || prevToolName
                            };
                            
                            return (
                                <div 
                                    key={message.id || index} 
                                    className={`p-4 ${
                                        message.role === 'user' && index === 0
                                            ? 'bg-primary/5 border-l-2 border-primary/30'
                                            : message.role === 'user'
                                                ? 'bg-transparent' 
                                                : message.role === 'tool' 
                                                    ? 'bg-[#131B2E]/70' 
                                                    : 'bg-[#131B2E]'
                                    }`}
                                >
                                    <div className="flex gap-3">
                                        <div className="flex-shrink-0 mt-1">
                                            <div
                                                className={`w-6 h-6 rounded flex items-center justify-center
                                                    ${message.role === 'user' && index === 0
                                                        ? 'bg-primary/20 text-primary'
                                                        : message.role === 'user' 
                                                            ? 'bg-primary text-white' 
                                                            : message.role === 'tool' 
                                                                ? 'bg-yellow-500/10 text-yellow-500'
                                                                : 'bg-primary/10 text-primary'
                                                    }`}
                                            >
                                                {message.role === 'user' && index === 0 
                                                    ? <FileText className="h-4 w-4" />
                                                    : message.role === 'user' 
                                                        ? <User className="h-4 w-4" /> 
                                                        : message.role === 'tool' 
                                                            ? <Terminal className="h-4 w-4" /> 
                                                            : <Bot className="h-4 w-4" />}
                                            </div>
                                        </div>
                                        <div className="flex-1 text-sm overflow-hidden">
                                            <div className="font-medium text-textlight mb-1">
                                                {/* If this is the first message in a sub-agent conversation, show "Task" */}
                                                {message.role === 'user' && index === 0 
                                                    ? 'Task' 
                                                    : message.role === 'user' 
                                                        ? user?.email || 'User' 
                                                        : message.role === 'tool' 
                                                            ? (message.tool_name || 'Tool') 
                                                            : 'Guardian'
                                                }
                                            </div>
                                            
                                            {/* Handle tool calls and responses with our custom renderer */}
                                            {message.role === 'tool' || message.tool_call_function_name ? (
                                                <ToolMessageRenderer 
                                                    message={enhancedMessage} 
                                                    isToolCall={message.tool_call || false} 
                                                />
                                            ) : (
                                                <>
                                                    {message.content && (
                                                        <div className="text-textmedium break-words overflow-hidden">
                                                            <MarkdownRenderer content={message.content} />
                                                        </div>
                                                    )}
                                                    {message.screenshot && (
                                                        <div className="mt-3 mb-4">
                                                            <div className="border border-[#1D2739] rounded-md p-1 bg-black/20 inline-block">
                                                                <img 
                                                                    src={message.screenshot.startsWith('data:') 
                                                                        ? message.screenshot 
                                                                        : `data:image/jpeg;base64,${message.screenshot}`
                                                                    } 
                                                                    alt="Screenshot" 
                                                                    className="max-w-full rounded-sm shadow-md"
                                                                />
                                                            </div>
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                        
                        {/* Messages end - scroll target */}
                        <div ref={messagesEndRef} />
                    </div>
                </VisibleScrollArea>
            )}
        </div>
    );
} 